import { Injectable } from '@angular/core';
import { ODataFilterBuilder } from 'odata-filter-builder';
import { ListHelperService, ProfileService, StoreService, StudentsService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { TableDatasource, DataTypes } from '@gamelearn/skeletor/models';
import { EntityQuestion } from 'core/components';

@Injectable()
export class StudentsListService {
  userRole: any;

  agentQuestion: EntityQuestion;
  partnerQuestion: EntityQuestion;
  searchAgentsComponent: any;
  searchPartnersComponent: any;
  constructor(
    public studentsService: StudentsService,
    public translate: TranslateService,
    public profileService: ProfileService,
    public listHelper: ListHelperService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  async getDatasource(listId, options: any = {}) {
    const dataSource = new TableDatasource({
      sort: 'name',
      columns: this.getColumns(),
      load: (props) => this.fetch(props, dataSource),
      storeState: {
        enabled: true,
        key: `${listId}_${this.userRole.name}`
      },
      ...options
    });
    await dataSource.refreshData();
    StoreService.create(listId, dataSource);
    return dataSource;
  }

  fetch({ page, limit, sort, search, filters, aggregates, populate }, dataSource: TableDatasource) {
    const { clientId, notInGroup, notTakenCourse, ...$filterValues } = filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    const paginationOptions = {
      page,
      limit,
      sort,
      search,
      aggregates,
      populate,
      clientId,
      notInGroup,
      notTakenCourse,
      $filter
    };
    return this.studentsService.fetch(paginationOptions).then((response) => response);
  }

  getDatasourceFilters(dataSource: TableDatasource) {
    const { clientId, notInGroup, ...$filterValues } = dataSource.filters;
    const $filter = this.listHelper.buildFilterString($filterValues, dataSource, this.getCustomFilters());
    return {
      clientId,
      notInGroup,
      $filter
    };
  }

  getColumns() {
    const columns = [
      {
        source: 'name',
        label: this.translate.instant('students.header.name'),
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'surname',
        label: this.translate.instant('students.header.surname'),
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'alias',
        label: this.translate.instant('students.header.alias'),
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'email',
        label: this.translate.instant('students.header.email'),
        sortable: true,
        style: { width: '200px' },
        cellRenderer: (renderer, value, student) =>
          student.suspended
            ? renderer`<div class="ellipsis" title="${value}">${value}</div>`
            : renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
                renderer,
                `/students/${student.id}`,
                value
              )}</div>`
      },
      {
        source: 'clientName',
        label: this.translate.instant('students.header.clientName'),
        sortable: true,
        visible: this.profileService.canExecute('students.tableColumns.clientName'),
        showInColumnChooser: this.profileService.canExecute('groups.tableColumns.clientName'),
        style: { width: '150px' },
        cellRenderer: (renderer, value, student) =>
          renderer`<div class="ellipsis">${this.listHelper.getRouterLinkRenderer(
            renderer,
            `/clients/${student.clientId}`,
            value
          )}</div>`
      },
      {
        source: 'created_at',
        label: this.translate.instant('properties.registerDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '140px' },
        valueParser: (value) => this.listHelper.parseDate(value)
      },
      {
        source: 'lastSessionDate',
        label: this.translate.instant('nouns.lastConnection'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '140px' },
        valueParser: (value) => this.listHelper.parseDate(value)
      },
      {
        source: 'studentStatus',
        label: this.translate.instant('nouns.status'),
        style: { width: '175px' },
        valueParser: (value, student) => {
          let status: string;
          if (student.anonymizedAt) {
            status = 'anonymized';
          } else if (student.suspended) {
            status = 'blocked';
          } else if (!student.active) {
            status = 'pending';
          } else if (student.hasPlayed) {
            status = 'inTraining';
          } else {
            status = 'activated';
          }
          return status;
        },
        cellRenderer: (renderer, value) =>
          renderer`
            <span style="color: var(${
              ['blocked', 'anonymized'].includes(value)
                ? '--coral'
                : ['pending'].includes(value)
                ? '--expert-orange'
                : '--aqua-marine'
            });">
              ${this.translate.instant(`students.status.${value}`)}
            </span>`,
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'active', value: this.translate.instant('students.status.activated') },
            { key: 'anonymizedAt', value: this.translate.instant('students.status.anonymized') },
            { key: 'blocked', value: this.translate.instant('students.status.blocked') },
            { key: 'inTraining', value: this.translate.instant('students.status.inTraining') },
            { key: 'pending', value: this.translate.instant('students.status.pending') }
          ];
          return renderer`
            <gl-select small multiselect
              .options="${options}"
              .value="${value || 'any'}"
              @onChange="${(e) => callback(e.detail.value)}"
            >
            </gl-select>`;
        }
      },
      {
        source: 'tags',
        label: this.translate.instant('nouns.tags'),
        dataType: DataTypes.array,
        style: { width: '175px' }
      }
    ];
    return columns;
  }

  getCustomFilters() {
    return {
      studentStatus: (filterBuilder, value) => {
        const fieldNames = {
          anonymizedAt: 'anonymizedAt',
          suspended: 'suspended',
          active: 'active',
          hasPlayed: 'hasPlayed'
        };
        const today = new Date().toISOString();
        const factory = {
          anonymizedAt: (statusFilterBuilder) => statusFilterBuilder.le(fieldNames.anonymizedAt, today),
          active: (statusFilterBuilder) =>
            statusFilterBuilder
              .and((fb) => fb.eq(fieldNames.suspended, false))
              .and((fb) => fb.eq(fieldNames.active, true))
              .and((fb) => fb.eq(fieldNames.hasPlayed, false))
              .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
          blocked: (statusFilterBuilder) =>
            statusFilterBuilder
              .and((fb) => fb.eq(fieldNames.suspended, true))
              .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
          pending: (statusFilterBuilder) =>
            statusFilterBuilder
              .and((fb) => fb.eq(fieldNames.suspended, false))
              .and((fb) => fb.eq(fieldNames.active, false))
              .and((fb) => fb.eq(fieldNames.anonymizedAt, null)),
          inTraining: (statusFilterBuilder) =>
            statusFilterBuilder
              .and((fb) => fb.eq(fieldNames.suspended, false))
              .and((fb) => fb.eq(fieldNames.active, true))
              .and((fb) => fb.eq(fieldNames.hasPlayed, true))
              .and((fb) => fb.eq(fieldNames.anonymizedAt, null))
        };
        if (value && value.length) {
          const sfb = ODataFilterBuilder();
          const filters = value && value.length ? value.map((v) => factory[v](ODataFilterBuilder())) : [];
          filters.forEach((f) => sfb.or(f));
          return filterBuilder.and(sfb);
        }
        return null;
      },
      tags: (filterBuilder, value) => (value ? value.forEach((v) => filterBuilder.contains('tags', v)) : null)
    };
  }
}
